import * as THREE from 'three'
import './style.css'
import { createPhysicsWorld } from './physics'
import { buildLevel, type Level } from './level'
import { buildBspLevel, type BspLoadedLevel } from './level/bsp-level'
import { PlayerController } from './player'
import { GameLoop } from './engine/gameLoop'
import { Scheduler } from './engine/scheduler'
import { enableTestMode, getTestMode, resolveCanvasSize, resolveClock } from './engine/testMode'
import { EngineEventBus } from './engine/event-bus'
import { DiagnosticsManager } from './engine/diagnostics'
import { EnemyManager } from './ai/enemy-manager'
import { GameHUD } from './ui/hud'
import { TutorialHintSystem } from './ui/tutorial-hints'
import { ProjectileManager } from './weapons/projectiles'
import { PickupManager } from './world/pickups'
import { CombatCoordinator } from './game/combat'
import { createSceneContext } from './engine/scene'
import { sfx } from './audio/sfx'
import { getMusicManager } from './audio/music-manager'
import { createDebugMaterialRegistry } from './debug/debug-materials'
import { SchematicCameraController } from './debug/schematic-view'
import { PauseMenu } from './ui/pause-menu'
import { GltfHotReloader } from './level/gltf-hot-reloader'
import type { SlidingDoor } from './world/doors'
import type { ElevatorController } from './world/elevator'
import type { DestructibleManager } from './world/destructibles'

const FIXED_TIMESTEP_MS = 1000 / 60

if (typeof window !== 'undefined') {
  ;(window as any).THREE = THREE
}

/**
 * Test mode is activated via URL params so a Playwright harness (or a manual QA
 * visit) can just navigate to e.g. `?testMode=1&seed=42` - no build-time branching.
 * Must run before anything else touches Math.random/performance.now/Date.now.
 */
function maybeEnableTestModeFromUrl(): void {
  const params = new URLSearchParams(window.location.search)
  if (params.get('testMode') !== '1') return

  enableTestMode({
    seed: params.has('seed') ? Number(params.get('seed')) : undefined,
    canvasWidth: params.has('canvasWidth') ? Number(params.get('canvasWidth')) : undefined,
    canvasHeight: params.has('canvasHeight') ? Number(params.get('canvasHeight')) : undefined,
  })
}

async function main() {
  maybeEnableTestModeFromUrl()
  const testMode = getTestMode()
  const params = new URLSearchParams(window.location.search)
  const requestedLevel = params.get('level') ?? 'tutorial'
  const isWhitebox = !!testMode && requestedLevel === 'whitebox'

  const { width, height } = resolveCanvasSize()
  const { scene, camera, renderer, stats } = createSceneContext(width, height, !!testMode)
  const world = await createPhysicsWorld()

  // 1. Debug Materials Registry & Schematic Top-Down Controller
  const debugRegistry = createDebugMaterialRegistry()
  const schematicCamera = new SchematicCameraController(scene, camera, width, height, debugRegistry)

  // Configure initial debug mode from URL parameters
  const debugParam = params.get('debug_textures') ?? params.get('debug')
  if (debugParam) {
    if (debugParam === 'normals' || debugParam === 'mesh_normals') {
      debugRegistry.setMode('mesh_normals')
    } else if (debugParam === 'wireframe' || debugParam === 'wireframe_normals') {
      debugRegistry.setMode('wireframe_normals')
    } else if (debugParam === 'types' || debugParam === 'type_colors' || debugParam === '1') {
      debugRegistry.setMode('type_colors')
    }
  }

  if (params.get('schematic') === '1' || params.get('topdown') === '1') {
    schematicCamera.setSchematic(true)
  }

  let activeDoors: SlidingDoor[] = []
  let activeElevators: ElevatorController[] = []
  let activeDestructibles: DestructibleManager
  let activeTargets: THREE.Mesh[] = []
  let spawnPos = new THREE.Vector3(0, 1.2, 0)
  let facingRad = 0

  const enemyManager = new EnemyManager(scene)
  const projectileManager = new ProjectileManager(scene, 128)
  const pickupManager = new PickupManager(scene)

  // Music system: the original engine addresses music by (level 0-9,
  // intensity 0-2) and nothing else. We expose the live enemy count so the
  // manager can drive the intensity row; the level index stays at its default
  // until the map -> music-level correspondence is recovered.
  // Selection changes are surfaced via the 'esoteria:musicState' window
  // CustomEvent; see src/audio/music-manager.ts.
  const music = getMusicManager()
  music.initialize()
  music.setCombatSource(() =>
    enemyManager.enemies.reduce((n, e) => (e.alive ? n + 1 : n), 0),
  )

  if (isWhitebox) {
    const level: Level = buildLevel(scene, world)
    activeDoors = level.doors
    activeDestructibles = level.destructibles
    activeTargets = level.targets
    spawnPos = new THREE.Vector3(0, 2, 4)
  } else {
    // Primary / Default Level: Reverse-Engineered Sector (defaults to ab00 airlock)
    const bspLevel: BspLoadedLevel = await buildBspLevel(scene, world, 0.05, debugRegistry, requestedLevel)
    activeDoors = bspLevel.doors
    activeElevators = bspLevel.elevators
    activeDestructibles = bspLevel.destructibleManager
    activeTargets = bspLevel.targets
    spawnPos = bspLevel.spawnPosition
    facingRad = bspLevel.facingRad ?? 0
  }

  const player = new PlayerController(camera, world, scene, spawnPos, facingRad)
  const clock = resolveClock()
  const scheduler = new Scheduler(clock)
  const loop = new GameLoop(clock, { fixedTimestepMs: FIXED_TIMESTEP_MS })

  // 2. Decoupled Diagnostics & Event Bus
  const eventBus = new EngineEventBus()
  const diagnostics = new DiagnosticsManager({
    bus: eventBus,
    context: { scene, camera, renderer, player, debugRegistry },
    debugRegistry,
    schematicCamera,
    enableInspector: true,
  })
  ;(window as unknown as { __macroRecorder?: unknown }).__macroRecorder = diagnostics.macroRecorder

  const combat = new CombatCoordinator({
    player,
    camera,
    projectiles: projectileManager,
    enemies: enemyManager,
    destructibles: activeDestructibles,
    pickups: pickupManager,
    targets: activeTargets,
    sfx,
    scheduler,
  })

  // Global helper for automation / scripts
  ;(window as any).__copyWthit = () => {
    return diagnostics.copyTargetedData(sfx)
  }

  activeDestructibles.onLootDrop((loot, pos) => {
    pickupManager.spawn(loot as any, pos.clone().setY(0.6))
  })

  let tutorialHints: TutorialHintSystem | null = null
  let pauseMenu: PauseMenu | null = null

  if (!testMode) {
    const hud = new GameHUD()
    hud.attach(player.vitality)
    hud.attachWeapons(player.weapons, player.vitality)
    hud.attachKeycards(player.keycards)

    const isTrainingLevel = requestedLevel.toLowerCase() === 'tutorial' || requestedLevel.toLowerCase() === 'training'
    if (isTrainingLevel) {
      tutorialHints = new TutorialHintSystem()

      // Tutorial Proving Ground Pickups
      pickupManager.spawn('health-tank', new THREE.Vector3(-10, 0.6, -5))
      pickupManager.spawn('energy-tank', new THREE.Vector3(-14, 0.6, -7))
      pickupManager.spawn('reserve-tank', new THREE.Vector3(4, 0.6, -5))
    }

    pauseMenu = new PauseMenu({
      currentLevel: requestedLevel,
      onSelectLevel: (levelName) => {
        const url = new URL(window.location.href)
        url.searchParams.set('level', levelName)
        window.location.href = url.toString()
      },
      onResume: () => {
        pauseMenu?.hide()
        player.controls.lock()
      },
      debugRegistry,
      schematicCamera,
    })
  }

  const overlay = document.getElementById('overlay')!
  overlay.addEventListener('click', () => {
    overlay.classList.add('hidden')
    pauseMenu?.hide()
    player.controls.lock()
    // First user gesture satisfies autoplay policy — start the music engine.
    if (!music.running) {
      void music.start()
    }
  })
  player.controls.addEventListener('lock', () => {
    overlay.classList.add('hidden')
    pauseMenu?.hide()
    if (!music.running) {
      void music.start()
    }
  })
  player.controls.addEventListener('unlock', () => {
    if (pauseMenu) {
      overlay.classList.add('hidden')
      pauseMenu.show()
    } else {
      overlay.classList.remove('hidden')
    }
  })

  if (params.get('clean') === '1' || params.get('no_hud') === '1' || params.get('hide_ui') === '1') {
    document.body.classList.add('hide-chrome')
  }

  // Keyboard Shortcuts for Weapons and Debug Diagnostics
  window.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' || event.code === 'Escape' || event.code === 'KeyP' || event.key === 'Tab') {
      if (pauseMenu) {
        event.preventDefault()
        const isShown = pauseMenu.toggle()
        if (isShown) {
          player.controls.unlock()
        } else {
          player.controls.lock()
        }
      }
    } else if (diagnostics.handleKeyDown(event, { world, sfx })) {
      // Handled by diagnostics subsystem (F1..F4, KeyC, KeyT)
    } else if (event.code.startsWith('Digit')) {
      // Weapon slot selection (1..9, 0)
      const digit = parseInt(event.code.replace('Digit', ''), 10)
      const slot = digit === 0 ? 10 : digit
      player.weapons.selectSlot(slot)
    } else if (event.code === 'KeyQ') {
      player.weapons.prevWeapon()
    } else if (event.code === 'KeyE') {
      player.weapons.nextWeapon()
    }
  })

  // Mouse wheel weapon switching
  window.addEventListener('wheel', (event) => {
    if (player.controls.isLocked) {
      if (event.deltaY > 0) {
        player.weapons.nextWeapon()
      } else if (event.deltaY < 0) {
        player.weapons.prevWeapon()
      }
    }
  })

  let isMouseDown = false
  document.addEventListener('mousedown', (event) => {
    if (event.button === 0 && player.controls.isLocked) {
      isMouseDown = true
      combat.tryFire()
    } else if (event.button === 1) {
      // Middle Click: ALWAYS WTHIT Copy JSON in dev env
      event.preventDefault()
      diagnostics.copyTargetedData(sfx)
    }
  })
  document.addEventListener('auxclick', (event) => {
    if (event.button === 1) {
      event.preventDefault()
    }
  })
  document.addEventListener('mouseup', (event) => {
    if (event.button === 0) {
      isMouseDown = false
    }
  })

  /**
   * Run one physics + per-system update tick. Shared between the normal
   * RAF loop (stepOnce) and the test-mode `advanceFrame(flushRender=false)`
   * path -- both previously inlined the same ~25-line update body.
   */
  const runPhysicsTick = (dt: number, render: (alpha: number) => void, alpha: number) => {
    player.update(dt)
    world.step()
    player.postPhysicsStep()
    music.tick()

    if (isMouseDown) {
      combat.tryFire()
    }

    for (let i = 0; i < activeDoors.length; i++) {
      const door = activeDoors[i]
      const dist = player.position.distanceTo(door.position)
      door.update(dt, dist < 3.5, player.keycards)
    }

    for (let i = 0; i < activeElevators.length; i++) {
      const elev = activeElevators[i]
      const dist = player.position.distanceTo(elev.position)
      elev.update(dt, dist < 2.5)
    }

    pickupManager.update(player, sfx)
    if (tutorialHints) {
      tutorialHints.update(player.position, sfx)
    }

    combat.updateProjectiles(dt, world)

    eventBus.emit('physicsTick', {
      dt,
      player,
      telemetry: player.getCollisionTelemetry(),
    })

    if (render) render(alpha)
  }

  const stepOnce = () => {
    stats.update()
    scheduler.update()
    loop.step({
      update: (dt) => {
        runPhysicsTick(dt, (alpha) => {
          player.render(alpha)
          pickupManager.animate(alpha)
          eventBus.emit('renderTick', {
            alpha,
            player,
            camera: schematicCamera.getCamera(),
          })
          renderer.render(scene, schematicCamera.getCamera())
        }, 0)
      },
      render: () => {},
    })
  }

  /**
   * Build the reload/probe bridge methods (copyTargetedData, reloadTargetedProp,
   * reloadProp, reloadAllProps, cycleTargetedSwatch). Shared by both the
   * test-mode and runtime __esoteria globals so the bridge surface stays in
   * one place.
   */
  const buildBridge = () => ({
    copyTargetedData() {
      return diagnostics.copyTargetedData(sfx)
    },
    reloadTargetedProp() {
      if (diagnostics.wthitInspector) {
        return GltfHotReloader.reloadTargetedProp(diagnostics.wthitInspector, scene, debugRegistry, world)
      }
      return Promise.resolve({ success: false, propId: 'none', message: 'No wthit inspector active' })
    },
    reloadProp(propId: string) {
      return GltfHotReloader.reloadPropInPlace(propId, scene, debugRegistry, world)
    },
    reloadAllProps() {
      return GltfHotReloader.reloadAllProps(scene, debugRegistry, world)
    },
    cycleTargetedSwatch(step = 1) {
      return diagnostics.wthitInspector ? diagnostics.wthitInspector.cycleTargetSwatch(step) : null
    },
  })

  if (testMode) {
    ;(window as unknown as { __esoteria: unknown }).__esoteria = {
      isTestMode: true,
      player,
      combat,
      debugRegistry,
      schematicCamera,
      setSchematic(enable: boolean) {
        schematicCamera.setSchematic(enable)
      },
      applyInputs(keys: string[], movementX = 0, movementY = 0, mouseDown = false) {
        player.setKeys(keys)
        if (movementX !== 0 || movementY !== 0) {
          player.applyMouseDelta(movementX, movementY)
        }
        if (mouseDown) {
          combat.tryFire()
        }
      },
      advanceFrame(dtMs = FIXED_TIMESTEP_MS, flushRender = true) {
        testMode.tick(dtMs)
        if (flushRender) {
          stepOnce()
        } else {
          scheduler.update()
          loop.step({
            update: (dt: number) => {
              runPhysicsTick(dt, undefined as unknown as (alpha: number) => void, 0)
            },
            render: () => {},
          })
        }
      },
      ...buildBridge(),
      musicPlayer: music,
    }
    renderer.render(scene, schematicCamera.getCamera())
  } else {
    ;(window as unknown as { __esoteria: unknown }).__esoteria = {
      player,
      combat,
      debugRegistry,
      schematicCamera,
      ...buildBridge(),
      musicPlayer: music,
    }
    renderer.setAnimationLoop(stepOnce)
  }
}

main().catch((err) => {
  console.error('Fatal initialization error in Esoteria Remaster:', err)
})
